import { load } from "js-yaml";
import { Enums } from "supabase/database.types";

import { API_BASE, NETLIFY_FUNCTIONS_BASE } from "@/constants/base-url";
import { Solution } from "@/hooks/useSolutionStore";

import type {
  ApiResponse,
  ContentModerationResult,
  GenerateWorksheetResult,
  TutorRequest,
  TutorResponse,
} from "./types";

export type QuizStreamEvent =
  | {
      count?: number;
      expected?: number;
      fromCache: boolean;
      practiceTestId?: null | string;
      subject: string;
      type: "init";
    }
  | { count?: number; type: "done" }
  | { error: string; index: number; type: "batch-error" }
  | { error: string; type: "error" }
  | { practiceTestId: null | string; type: "saved" }
  | {
      question: {
        correctAnswer: string;
        explanation?: string;
        id: string;
        options: {
          A: string;
          B: string;
          C: string;
          D: string;
        };
        question: string;
      };
      type: "question";
    };

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {},
  timeoutMs: number = 30000 // Default 30 second timeout
): Promise<ApiResponse<T>> {
  try {
    // Create an AbortController for timeout handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const response = await fetch(endpoint, {
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      signal: controller.signal,
      ...options,
    });

    clearTimeout(timeoutId);

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(
        data.error || "An error occurred",
        response.status,
        data
      );
    }

    return {
      data,
      status: response.status,
      success: true,
    };
  } catch (error) {
    console.error("API Error:", error);

    if (error instanceof ApiError) {
      return {
        error: error.message,
        status: error.status,
        success: false,
      };
    }

    // Handle timeout specifically
    if (error instanceof Error && error.name === "AbortError") {
      return {
        error: "Request timed out. Please try again.",
        status: 408,
        success: false,
      };
    }

    return {
      error: error instanceof Error ? error.message : "Unknown error occurred",
      status: 500,
      success: false,
    };
  }
}

export async function callChatApi(
  message: string,
  subject?: string
): Promise<ApiResponse<{ message: string; timestamp: string }>> {
  return apiCall<{ message: string; timestamp: string }>(`${API_BASE}/chat`, {
    body: JSON.stringify({ message, subject }),
    method: "POST",
  });
}

export async function callTutorApi(
  request: TutorRequest
): Promise<ApiResponse<TutorResponse>> {
  return apiCall<TutorResponse>(`${API_BASE}/ai-tutor`, {
    body: JSON.stringify(request),
    method: "POST",
  });
}

export async function generateFlashcards(
  subjectSlug: null | string,
  topic: null | string,
  difficulty: Enums<"difficulty">,
  type: "fill-in-the-blank" | "mixed" | "multiple-choice",
  customPrompt?: string
): Promise<
  ApiResponse<{
    flashcards: Array<{
      back: string;
      difficulty: string;
      front: string;
      id: string;
    }>;
  }>
> {
  return apiCall<{
    flashcards: Array<{
      back: string;
      difficulty: string;
      front: string;
      id: string;
    }>;
  }>(`${API_BASE}/generate-flashcards`, {
    body: JSON.stringify({
      customPrompt,
      difficulty,
      subjectSlug,
      topic,
      type,
    }),
    method: "POST",
  });
}

export async function generateHint(
  question: string,
  answer: string
): Promise<ApiResponse<{ hint: string }>> {
  return apiCall<{ hint: string }>(`${API_BASE}/create-hint`, {
    body: JSON.stringify({
      answer,
      question,
    }),
    method: "POST",
  });
}

export async function generateQuiz(request: {
  count?: number;
  subject: string;
}) {
  return apiCall<{
    count: number;
    fromCache?: boolean;
    practiceTestId?: null | string;
    questions: Array<{
      correctAnswer: string;
      explanation?: string;
      id: string;
      options: {
        A: string;
        B: string;
        C: string;
        D: string;
      };
      question: string;
    }>;
    subject: string;
  }>(`${NETLIFY_FUNCTIONS_BASE}/generate-quiz`, {
    body: JSON.stringify(request),
    method: "POST",
  });
}

export async function generateWorksheet(request: {
  customTopic?: string;
  difficulty: Enums<"difficulty">;
  grade: number;
  problemCount: number;
  subject: string;
}) {
  return apiCall<GenerateWorksheetResult>(`${API_BASE}/generate-worksheet`, {
    body: JSON.stringify(request),
    method: "POST",
  });
}

export async function getPopularQuestions(params: {
  exclude?: string[];
  subject?: string;
}): Promise<ApiResponse<{ questions: string[]; subject: string }>> {
  const qs = new URLSearchParams();
  if (params.subject) qs.set("subject", params.subject);
  if (params.exclude && params.exclude.length > 0)
    qs.set("exclude", params.exclude.join("|"));

  return apiCall<{ questions: string[]; subject: string }>(
    `${API_BASE}/ai-tutor/popular-questions?${qs.toString()}`,
    { method: "GET" }
  );
}

export async function getSubjects(): Promise<
  ApiResponse<{ subjects: Array<{ id: string; name: string; slug: string }> }>
> {
  return apiCall<{
    subjects: Array<{ id: string; name: string; slug: string }>;
  }>(`${API_BASE}/subjects`, {
    method: "GET",
  });
}

export async function moderateContent(content: string) {
  return apiCall<ContentModerationResult>(`${API_BASE}/moderate-content`, {
    body: JSON.stringify({ content }),
    method: "POST",
  });
}

export function parseYAMLFrontmatter<TMetadata extends Record<string, unknown>>(
  content: string
): {
  body: string;
  metadata: TMetadata;
} {
  // Pick out any YAML frontmatter delimited by ---
  const frontmatterRegex =
    /^((?:(?!---)[\s\S])*?)(?:---\s*\n((?:(?!---|\n\n)[\s\S])*)\n)(?:---)?\s*([\s\S]*)?$/m;
  const match = content.match(frontmatterRegex);

  if (!match) {
    // No frontmatter found, treat entire content as body
    return {
      body: content,
      metadata: {} as TMetadata,
    };
  }

  const [_, beforeBody, yamlContent, afterBody] = match;
  const body = [beforeBody ?? "", afterBody ?? ""].join("\n\n").trim();

  // Simple YAML parser for our specific case
  const metadata = yamlContent
    ? (load(yamlContent) as TMetadata)
    : ({} as TMetadata);

  return {
    body,
    metadata,
  };
}

export async function recordQotdAttempt(params: {
  correctAnswerText?: string;
  isCorrect: boolean;
  questionId: number | string;
  questionSlug: string;
  selectedAnswerText?: string;
  subjectSlug: string;
  timeSeconds?: number;
}) {
  return apiCall<{ success: boolean }>(`${API_BASE}/qotd/attempts`, {
    body: JSON.stringify(params),
    method: "POST",
  });
}

export async function regenerateProblem(request: {
  answer: string;
  difficulty: string;
  grade: number;
  question: string;
  subject: string;
  type: string;
}) {
  return apiCall<{
    answer: string;
    question: string;
  }>(`${API_BASE}/regenerate-problem`, {
    body: JSON.stringify(request),
    method: "POST",
  });
}

export async function sendChallengeInvite(params: {
  accuracy?: number;
  challengerName?: string;
  challengeUrl: string;
  targetEmail: string;
}): Promise<ApiResponse<{ message: string; success: boolean }>> {
  return apiCall<{ message: string; success: boolean }>(
    `${API_BASE}/challenge/invite`,
    {
      body: JSON.stringify(params),
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
    }
  );
}

export async function sendTutorResponseFeedback(params: {
  isHelpful: boolean;
  response: string;
}) {
  return apiCall<{ id: null | string; success: boolean }>(
    `${API_BASE}/ai-tutor/response-feedback`,
    {
      body: JSON.stringify(params),
      method: "POST",
    }
  );
}

export async function solveWithImage(imageData: string) {
  return apiCall<Solution>(`${API_BASE}/solve-with-image`, {
    body: JSON.stringify({ imageData }),
    method: "POST",
  }, 60000); // 60 second timeout for AI operations
}

export async function solveWithText(problem: string) {
  return apiCall<Solution>(`${API_BASE}/solve-with-text`, {
    body: JSON.stringify({ inputType: "text", problem }),
    method: "POST",
  }, 60000); // 60 second timeout for AI operations
}

export async function* streamGenerateQuiz(
  request: { count?: number; subject: string },
  options?: { signal?: AbortSignal }
): AsyncGenerator<QuizStreamEvent, void, unknown> {
  try {
    const response = await fetch(
      `${NETLIFY_FUNCTIONS_BASE}/generate-quiz?stream=true`,
      {
        body: JSON.stringify(request),
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        signal: options?.signal,
      }
    );

    if (!response.ok) {
      const error = await response.text().catch(() => "");
      throw new ApiError(error || "Stream request failed", response.status);
    }

    if (!response.body) {
      throw new Error("No response body for streaming");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        let newlineIndex: number;
        while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
          const line = buffer.slice(0, newlineIndex).trim();
          buffer = buffer.slice(newlineIndex + 1);
          if (!line) continue;
          try {
            const event = JSON.parse(line) as QuizStreamEvent;
            yield event;
          } catch (_error) {
            // Ignore malformed line and continue
          }
        }
      }

      // Flush any remaining buffered line
      const finalLine = buffer.trim();
      if (finalLine.length > 0) {
        try {
          const event = JSON.parse(finalLine) as QuizStreamEvent;
          yield event;
        } catch {
          // ignore
        }
      }
    } finally {
      reader.releaseLock();
    }
  } catch (error) {
    // Swallow user-initiated aborts quietly
    const name = (error as { name?: string })?.name;
    if (name === "AbortError") {
      return;
    }
    console.error("Streaming Error (generate-quiz):", error);
    throw error;
  }
}
